import axios from "axios";

/**
 * Platform API Client for sovereign platform administration.
 * Configured with withCredentials: true for RFC 6265bis __Host-Homy-Platform-Session cookie,
 * and support for dynamic X-Organization-Id tenant scoping.
 */

let activeScopedOrgId = null;

export function setPlatformScopedOrganization(organizationId) {
  activeScopedOrgId = organizationId;
}

export function getPlatformScopedOrganization() {
  return activeScopedOrgId;
}

export function createPlatformClient(options = {}) {
  const envPlatformUrl =
    (typeof import.meta !== "undefined" && import.meta?.env?.VITE_PLATFORM_API_URL) ||
    (typeof process !== "undefined" && process?.env?.VITE_PLATFORM_API_URL);

  const envApiUrl =
    (typeof import.meta !== "undefined" && import.meta?.env?.VITE_API_URL) ||
    (typeof process !== "undefined" && process?.env?.VITE_API_URL);

  const resolvedDefaultBaseURL =
    envPlatformUrl ||
    (envApiUrl ? `${envApiUrl.replace(/\/$/, "")}/api/platform` : "/api/platform");

  const baseURL = options.baseURL || resolvedDefaultBaseURL;
  const client = axios.create({
    baseURL,
    withCredentials: true,
    headers: {
      "Content-Type": "application/json",
      ...(options.headers || {}),
    },
    ...options,
  });

  // Request interceptor: inject X-Organization-Id if set
  client.interceptors.request.use(
    (config) => {
      if (activeScopedOrgId && !config.headers["X-Organization-Id"]) {
        config.headers["X-Organization-Id"] = activeScopedOrgId;
      }
      return config;
    },
    (error) => Promise.reject(error),
  );

  return client;
}

export const platformClient = createPlatformClient();

/**
 * Platform Support API Client Methods
 * Targets /api/platform/support/* endpoints guarded by require_platform_admin.
 */

/**
 * List platform support tickets with optional filters and pagination.
 * @param {Object} [params] - Query filters (status, priority, category, channel, submittingOrganizationId, assignedTo, search, isSlaBreached, page, limit)
 * @param {import("axios").AxiosInstance} [client] - Optional axios instance override
 * @returns {Promise<{items: Array<Object>, total: number, page: number, limit: number}>}
 */
export async function getPlatformTickets(params = {}, client = platformClient) {
  const response = await client.get("/support/tickets", { params });
  return response.data;
}

export const listPlatformTickets = getPlatformTickets;

/**
 * Retrieve system-wide platform support operational KPIs.
 * @param {import("axios").AxiosInstance} [client]
 * @returns {Promise<{totalOpen: number, urgentCount: number, slaBreachedCount: number, averageResponseTimeHours: number|null, averageResolutionTimeHours: number|null, casesByCategory: Object}>}
 */
export async function getPlatformTicketMetrics(client = platformClient) {
  const response = await client.get("/support/metrics");
  return response.data;
}

export const getPlatformSupportMetrics = getPlatformTicketMetrics;

/**
 * Retrieve detailed platform support ticket including message history and submitting org metadata.
 * @param {string} ticketId - UUID or ID of the platform ticket
 * @param {import("axios").AxiosInstance} [client]
 * @returns {Promise<Object>}
 */
export async function getPlatformTicket(ticketId, client = platformClient) {
  const response = await client.get(`/support/tickets/${ticketId}`);
  return response.data;
}

/**
 * Full update of platform ticket metadata and fields.
 * @param {string} ticketId
 * @param {Object} data - Update payload (status, priority, category, subject, assignedToId, resolutionNotes, tags, metadata)
 * @param {import("axios").AxiosInstance} [client]
 * @returns {Promise<Object>}
 */
export async function updatePlatformTicket(ticketId, data, client = platformClient) {
  const response = await client.put(`/support/tickets/${ticketId}`, data);
  return response.data;
}

/**
 * Transition platform ticket status.
 * @param {string} ticketId
 * @param {string|{status: string, resolutionNotes?: string}} statusOrPayload
 * @param {import("axios").AxiosInstance} [client]
 * @returns {Promise<Object>}
 */
export async function updatePlatformTicketStatus(ticketId, statusOrPayload, client = platformClient) {
  const payload =
    typeof statusOrPayload === "string"
      ? { status: statusOrPayload }
      : statusOrPayload;
  const response = await client.patch(`/support/tickets/${ticketId}/status`, payload);
  return response.data;
}

/**
 * Assign platform ticket to an operator.
 * @param {string} ticketId
 * @param {string|null|{assignedToId: string|null}} assigneeOrPayload
 * @param {import("axios").AxiosInstance} [client]
 * @returns {Promise<Object>}
 */
export async function assignPlatformTicket(ticketId, assigneeOrPayload, client = platformClient) {
  const payload =
    typeof assigneeOrPayload === "object" && assigneeOrPayload !== null
      ? assigneeOrPayload
      : { assignedToId: assigneeOrPayload };
  const response = await client.patch(`/support/tickets/${ticketId}/assign`, payload);
  return response.data;
}

/**
 * Post an operator outbound reply or internal operator note.
 * @param {string} ticketId
 * @param {Object} messagePayload - { content: string, direction?: string, isInternal?: boolean, attachments?: any }
 * @param {import("axios").AxiosInstance} [client]
 * @returns {Promise<Object>}
 */
export async function addPlatformTicketMessage(ticketId, messagePayload, client = platformClient) {
  const response = await client.post(`/support/tickets/${ticketId}/messages`, messagePayload);
  return response.data;
}

/**
 * List message timeline for a platform ticket.
 * @param {string} ticketId
 * @param {import("axios").AxiosInstance} [client]
 * @returns {Promise<Array<Object>>}
 */
export async function getPlatformTicketMessages(ticketId, client = platformClient) {
  const response = await client.get(`/support/tickets/${ticketId}/messages`);
  return response.data;
}

export const listPlatformTicketMessages = getPlatformTicketMessages;

export const platformSupportApi = {
  getTickets: getPlatformTickets,
  listTickets: getPlatformTickets,
  getMetrics: getPlatformTicketMetrics,
  getTicket: getPlatformTicket,
  updateTicket: updatePlatformTicket,
  updateTicketStatus: updatePlatformTicketStatus,
  assignTicket: assignPlatformTicket,
  addTicketMessage: addPlatformTicketMessage,
  getTicketMessages: getPlatformTicketMessages,
  listTicketMessages: getPlatformTicketMessages,
};

